import { useEffect } from "react";
import { Navigate, useNavigate, useLocation } from "react-router";
import { Helmet } from "react-helmet-async";
import { computeLoginRedirectTarget } from "@/lib/loginRedirect";
import { isTokenExpired } from "@/lib/session";

/**
 * ProtectedRoute — guards any authenticated page.
 * If there is no compass_token the user is sent to /login and the
 * URL they were trying to reach is preserved as `?next=<path>` so
 * the login form can redirect them back after auth (LoginPage's
 * getNextPath() already reads this).
 * No per-user-type gating here; all portal pages share the same URL
 * space and each page renders its own <AccessDenied /> screen when
 * the logged-in user can't view the specific resource.
 *
 * Session 116 — also listens for cross-tab `storage` events. When
 * another tab logs out (removes `compass_token`), every other tab
 * sitting on a protected page navigates to `/` instantly — matches
 * Gmail / Slack / Notion behaviour and prevents stale-session
 * confusion (e.g. the user logs out on their phone-mirrored tab
 * and expects the desktop to follow along without waiting for the
 * next 401).
 */
export default function ProtectedRoute({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("compass_token");

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "compass_token" && !e.newValue) {
        navigate("/", { replace: true });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [navigate]);

  if (!token || isTokenExpired(token)) {
    // No token, OR a token whose `exp` has already passed. An
    // already-expired token means the user is returning to a signed-in
    // page after their session lapsed (a "cold revisit", often days
    // later). Rather than pop the in-session "session expired" modal
    // (which is meant for a session that dies WHILE the page is open),
    // treat them as logged out and bounce to /login — preserving the
    // destination as `?next=` so they land back here after signing in.
    //
    // Clear the stale token so nothing else mistakes them for signed in,
    // but only if it still matches what we read (a concurrent login or
    // another tab may have just written a fresh one — don't clobber it).
    if (token && localStorage.getItem("compass_token") === token) {
      try { localStorage.removeItem("compass_token"); } catch { /* ignore */ }
    }
    const dest = `${location.pathname}${location.search}`;
    return <Navigate to={computeLoginRedirectTarget(dest)} replace />;
  }
  // Nothing behind the login may be indexed. robots.txt Disallow only asks a
  // crawler not to FETCH the page — a URL that leaks (an email forward, a
  // referrer, a pasted link) can still be listed from the link alone. This
  // tag is the directive that actually removes it, and it rides on every
  // portal page because they all mount through here.
  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
        <meta name="googlebot" content="noindex, nofollow, noarchive" />
      </Helmet>
      {children}
    </>
  );
}
